import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useSpin } from '@/components/SpinContext';
import { PostShipmentsCalendar } from '@/apis/shipment';
import type {
  CalendarDayAggregate,
  CalendarShipmentsApiResponse,
  CalendarViewBy,
  ShipmentFiltersPayload,
} from '../types';
import { filterItemsByLegType } from '../legTypeUtils';

type UseCalendarShipmentsArgs = {
  token: string;
  unauthorize: (error: unknown) => void;
  legType?: 'CL' | 'EL';
  filtersPayloadRef?: React.MutableRefObject<ShipmentFiltersPayload | null>;
  activeTabId?: string;
  sortKey?: string;
  sortOrder?: 'ascending' | 'descending';
  enabled?: boolean;
  filtersKey?: string;
  searchQuery?: string;
  hasActiveFilters?: boolean;
  onFetchSuccess?: () => void;
};

export const useCalendarShipments = ({
  token,
  unauthorize,
  legType = 'CL',
  filtersPayloadRef,
  activeTabId,
  sortKey,
  sortOrder,
  enabled = true,
  filtersKey = '',
  searchQuery = '',
  hasActiveFilters = false,
  onFetchSuccess,
}: UseCalendarShipmentsArgs) => {
  const { showSpin, hideSpin } = useSpin();

  const [calendarData, setCalendarData] = useState<CalendarDayAggregate[]>([]);
  const [calendarTotalShipments, setCalendarTotalShipments] =
    useState<number>(0);
  const [calendarCounts, setCalendarCounts] = useState<{
    critical: number;
    at_risk: number;
    no_issue: number;
  } | null>(null);

  const [viewBy, setViewBy] = useState<CalendarViewBy>('pickup_date');

  const inFlightRef = useRef(false);
  const lastReqKeyRef = useRef('');

  const trimmedSearch = useMemo(
    () => (searchQuery ?? '').trim(),
    [searchQuery]
  );

  useEffect(() => {
    if (!enabled) {
      lastReqKeyRef.current = '';
      setCalendarData([]);
      setCalendarTotalShipments(0);
      setCalendarCounts(null);
      inFlightRef.current = false;
    }
  }, [enabled]);

  const fetchCalendarShipments = useCallback(
    async (
      view_by?: CalendarViewBy,
      payloadOverride?: ShipmentFiltersPayload | null,
      dateRange?: { start: string; end: string },
      force?: boolean,
      viewType?: string,
      tabIdOverride?: string | null,
      sortKeyOverride?: string,
      sortOrderOverride?: 'ascending' | 'descending',
      legTypeOverride?: 'CL' | 'EL'
    ) => {
      if (!enabled) return;
      if (inFlightRef.current) return;

      const effective = view_by ?? viewBy;
      const effectiveLegType = legTypeOverride ?? legType;
      const effectiveSortKey = sortKeyOverride ?? sortKey;
      const effectiveSortOrder = sortOrderOverride ?? sortOrder;

      const extraPayload =
        payloadOverride && typeof payloadOverride === 'object'
          ? payloadOverride
          : filtersPayloadRef?.current &&
              typeof filtersPayloadRef.current === 'object'
            ? filtersPayloadRef.current
            : {};

      const effectiveTabId = tabIdOverride ?? activeTabId;
      const reqKey = JSON.stringify({
        view_by: effective,
        leg_type: effectiveLegType,
        tab_id: effectiveTabId,
        filtersKey,
        search: trimmedSearch,
        sort_key: effectiveSortKey,
        sort_order: effectiveSortOrder,
        payload: extraPayload,
        dateRange,
        viewType,
      });

      if (!force && lastReqKeyRef.current === reqKey) return;
      lastReqKeyRef.current = reqKey;

      inFlightRef.current = true;
      showSpin();
      if (hasActiveFilters) setCalendarTotalShipments(0);

      try {
        const res: unknown = await PostShipmentsCalendar(token, {
          view_by: effective,
          leg_type: effectiveLegType,
          search: trimmedSearch,
          ...(effectiveSortKey ? { sort_key: effectiveSortKey } : {}),
          ...(effectiveSortOrder ? { sort_order: effectiveSortOrder } : {}),
          ...(effectiveTabId === 'active' ? { tab_id: effectiveTabId } : {}),
          ...extraPayload,
          ...(dateRange ?? {}),
          ...(viewType ? { view_type: viewType } : {}),
        });

        const apiRes = res as CalendarShipmentsApiResponse;
        const env = apiRes?.data?.data;

        if (!env) return;

        if (env.is_success) {
          const dataByLegType = (env.data ?? []).map((day) => ({
            ...day,
            top_shipments: filterItemsByLegType(
              day.top_shipments ?? [],
              effectiveLegType
            ),
            hour_aggregates: (day.hour_aggregates ?? []).map(
              (hourAggregate) => ({
                ...hourAggregate,
                top_shipments: filterItemsByLegType(
                  hourAggregate.top_shipments ?? [],
                  effectiveLegType
                ),
              })
            ),
          }));
          setCalendarData(dataByLegType);
          setCalendarCounts(env.counts ?? null);
          // The response is already scoped to the visible date range, leg
          // type, tab, search, and filters. Preserve its authoritative total
          // for both filtered and unfiltered calendar toolbar counts.
          setCalendarTotalShipments(Number(env.total_shipment_count) || 0);
          onFetchSuccess?.();
        } else if (env.message) {
          message.error(env.message);
          setCalendarData([]);
          setCalendarTotalShipments(0);
          setCalendarCounts(null);
        }
      } catch (error: unknown) {
        unauthorize(error);
      } finally {
        inFlightRef.current = false;
        hideSpin();
      }
    },
    [
      enabled,
      viewBy,
      legType,
      filtersPayloadRef,
      activeTabId,
      sortKey,
      sortOrder,
      filtersKey,
      trimmedSearch,
      showSpin,
      token,
      unauthorize,
      hideSpin,
      hasActiveFilters,
      onFetchSuccess,
    ]
  );

  return {
    calendarData,
    calendarTotalShipments,
    calendarCounts,
    viewBy,
    setViewBy,
    fetchCalendarShipments,
  };
};
